import { Component, Inject, OnInit } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { lastValueFrom } from "rxjs";
import { IMcpOAuthClient, StakeholderServicesService } from "../stakeholder-services.service";

@Component({
    selector: "adapt-mcp-oauth-client-dialog",
    templateUrl: "./mcp-oauth-client-dialog.component.html",
    standalone: false,
})
export class McpOAuthClientDialogComponent extends BaseDialogComponent<IMcpOAuthClient | undefined, IMcpOAuthClient> implements OnInit {
    public readonly dialogName = "McpOAuthClient";
    public readonly isEditing: boolean;
    public readonly isConfigurationManaged: boolean;
    public readonly grantTypes = "authorization_code, refresh_token";
    public readonly responseTypes = "code";
    public readonly isConfidential: boolean;
    public generateClientSecret = false;
    public savedClient?: IMcpOAuthClient;
    public existingClientSecret?: string;
    public loadingClientSecret = false;
    public clientSecretError?: string;

    public get tokenEndpointAuthMethod() {
        return this.isConfidential || this.generateClientSecret
            ? "client_secret_post, client_secret_basic"
            : "none (public client)";
    }

    public clientId: string;
    public displayName: string;
    public redirectUris: string;

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) client: IMcpOAuthClient | undefined,
        private stakeholderServicesService: StakeholderServicesService,
        private commonDialogService: AdaptCommonDialogService,
    ) {
        super();
        this.isEditing = !!client;
        this.isConfidential = client?.IsConfidential ?? false;
        this.isConfigurationManaged = client?.IsConfigurationManaged ?? false;
        this.clientId = client?.ClientId ?? "";
        this.displayName = client?.DisplayName ?? "";
        this.redirectUris = client?.RedirectUris.join("\n") ?? "";
    }

    public async ngOnInit() {
        if (!this.isEditing || !this.isConfidential) {
            return;
        }

        this.loadingClientSecret = true;
        try {
            const result = await this.stakeholderServicesService.getMcpOAuthClientSecret(this.clientId);
            this.existingClientSecret = result.ClientSecret ?? undefined;
        } catch (e) {
            this.clientSecretError = ErrorHandlingUtilities.getHttpResponseMessage(e);
        } finally {
            this.loadingClientSecret = false;
        }
    }

    public finish() {
        if (this.savedClient) {
            const { ClientSecret: _, ...client } = this.savedClient;
            this.savedClient = undefined;
            this.resolve(client);
        }
    }

    @Autobind
    public async saveClient() {
        const redirectUris = this.redirectUris
            .split(/\r?\n/)
            .map((uri) => uri.trim())
            .filter((uri) => !!uri);

        if (!this.clientId.trim() || !this.displayName.trim() || !redirectUris.length) {
            this.setErrorMessage("Client ID, client name, and at least one redirect URI are required.");
            return;
        }

        if (this.isConfigurationManaged) {
            const dialogData: IConfirmationDialogData = {
                title: "Save configuration-managed client",
                message: "This client is managed by server configuration. Your changes will be restored from configuration when the server next starts.",
                confirmButtonText: "Save anyway",
            };
            if (!await lastValueFrom(this.commonDialogService.openConfirmationDialogWithBoolean(dialogData))) {
                return;
            }
        }

        this.clearErrorMessage();
        try {
            const savedClient = await this.stakeholderServicesService.putMcpOAuthClient(this.clientId.trim(), {
                displayName: this.displayName.trim(),
                redirectUris,
                generateClientSecret: this.generateClientSecret,
            });
            if (savedClient.ClientSecret) {
                this.savedClient = savedClient;
            } else {
                this.resolve(savedClient);
            }
        } catch (e) {
            this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(e));
        }
    }
}
